/*
 * eme-dragscroll.js
 * -----------------------------------------------------------------------------
 * Mouse-drag scrolling for every horizontal rail in the theme.
 *
 * WHY THIS EXISTS
 * EME drives all of their horizontal rails with Embla, and five of those setups
 * pass `dragFree: true` (grep _qa/eme/js for `dragFree:!0`, plus one full option
 * object `{align:'center',containScroll:'trimSnaps',dragFree:!0,loop:c??!1}`).
 * dragFree means you grab the rail with the mouse and fling it; it does not snap.
 *
 * Our rails are native `overflow-x: auto` containers. That is behaviour-identical
 * to Embla on TOUCH and on a trackpad, which is why it was chosen, but a mouse
 * cannot scroll a native container sideways at all: there is no horizontal wheel
 * axis on a normal mouse and click-drag does nothing. So on a desktop with a mouse
 * the carousel, Shop the look and the Instagram rail looked frozen, with content
 * visibly cut off at the right edge and no way to reach it.
 *
 * This adds the missing input mode and nothing else. Touch is left completely
 * alone (pointerType is checked), so native momentum scrolling keeps working on
 * phones exactly as before.
 *
 * DESIGN NOTES
 * - Click suppression is distance-gated. A drag of a few pixels is a click, so
 *   links inside the rail still work; past the threshold the trailing click is
 *   swallowed once, otherwise every fling would also open a product.
 * - The inertia is a plain exponential decay on the last measured velocity. Embla
 *   uses a friction model; at these distances the two are indistinguishable, and
 *   this keeps the file dependency-free.
 * - prefers-reduced-motion drops the inertia and leaves the direct 1:1 drag.
 */
(() => {
  if (window.__emeDragScroll) return;
  window.__emeDragScroll = true;

  /* Every horizontal rail in the theme. Kept here rather than as an attribute on
     each section so there is one list to maintain instead of eight Liquid edits. */
  const SELECTORS = [
    '.eme-carousel__scroller',
    '.eme-stl__scroller',
    '.eme-ig__scroller',
    '.eme-rail__scroller',
    '.eme-slider__scroller',
    '.eme-tabs',
    '.eme-pdp__look-grid',
    '.eme-pdp__reco-scroller',
    '.eme-cart__upsell-scroller',
    '[data-eme-dragscroll]',
  ].join(',');

  const DRAG_THRESHOLD = 4; // px before this becomes a drag rather than a click
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  const bind = (el) => {
    if (el.dataset.emeDragBound === 'true') return;
    el.dataset.emeDragBound = 'true';

    let pointerId = null;
    let startX = 0;
    let startScroll = 0;
    let moved = 0;
    let lastX = 0;
    let lastT = 0;
    let velocity = 0;
    let raf = 0;

    const stopInertia = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
    };

    const glide = () => {
      velocity *= 0.95;
      if (Math.abs(velocity) < 0.15) { raf = 0; return; }
      el.scrollLeft -= velocity;
      raf = requestAnimationFrame(glide);
    };

    el.addEventListener('pointerdown', (e) => {
      // Touch and pen already scroll natively and much better. Mouse only.
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      // Nothing to drag when the content fits.
      if (el.scrollWidth <= el.clientWidth + 1) return;
      stopInertia();
      pointerId = e.pointerId;
      startX = lastX = e.clientX;
      startScroll = el.scrollLeft;
      lastT = e.timeStamp;
      moved = 0;
      velocity = 0;
      el.classList.add('eme-dragging');
    });

    el.addEventListener('pointermove', (e) => {
      if (pointerId === null || e.pointerId !== pointerId) return;
      const dx = e.clientX - startX;
      if (!moved && Math.abs(dx) < DRAG_THRESHOLD) return;
      if (!moved) {
        // Only capture once we are certain this is a drag, so a plain click on a
        // card is never stolen from the link underneath it.
        moved = 1;
        try { el.setPointerCapture(pointerId); } catch (err) { /* capture is best effort */ }
      }
      moved = Math.max(moved, Math.abs(dx));
      const dt = e.timeStamp - lastT;
      // Clamp. A very small dt (coalesced or synthetic events) makes px/ms explode
      // and the rail flings straight to the end, which feels broken rather than free.
      if (dt > 0) velocity = Math.max(-60, Math.min(60, (e.clientX - lastX) / dt * 16));
      lastX = e.clientX;
      lastT = e.timeStamp;
      el.scrollLeft = startScroll - dx;
      e.preventDefault();
    });

    const end = (e) => {
      if (pointerId === null || (e && e.pointerId !== pointerId)) return;
      try { el.releasePointerCapture(pointerId); } catch (err) { /* already released */ }
      pointerId = null;
      el.classList.remove('eme-dragging');
      if (moved > DRAG_THRESHOLD) {
        el.dataset.emeDragged = 'true';
        // Clear on the next tick, after the trailing click has been swallowed.
        setTimeout(() => { delete el.dataset.emeDragged; }, 0);
        if (!reduced.matches && Math.abs(velocity) > 1) glide();
      }
      moved = 0;
    };

    el.addEventListener('pointerup', end);
    el.addEventListener('pointercancel', end);
    el.addEventListener('lostpointercapture', end);

    el.addEventListener('click', (e) => {
      if (el.dataset.emeDragged === 'true') {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);

    // Native image dragging fights the gesture and shows a ghost image.
    el.addEventListener('dragstart', (e) => {
      if (el.classList.contains('eme-dragging')) e.preventDefault();
    });
  };

  const scan = (root) => {
    (root || document).querySelectorAll(SELECTORS).forEach(bind);
  };

  const start = () => {
    scan(document);
    // Sections re-render in the theme editor and the cart drawer swaps its own
    // markup on every update, so keep watching rather than binding once.
    new MutationObserver((records) => {
      for (const r of records) {
        r.addedNodes.forEach((n) => {
          if (!(n instanceof HTMLElement)) return;
          if (n.matches && n.matches(SELECTORS)) bind(n);
          scan(n);
        });
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
